import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";
import SamplePdfModal from "../../Components/SamplePdfModal/SamplePdfModal";
import ContentProductVideoAi from "../Gallery/ContentProductVideoAi";
import { saveAs } from "file-saver";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoIcon from "@mui/icons-material/Info";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import "./NewProductVideoAI.css";
import swal from "sweetalert";
import { DownloadOutlined } from "@mui/icons-material";

const API_BASE =
    "https://backend.whalesai.com/api/v1/product-video-ai";

const MODEL_OPTIONS = [
    {
        value: "kling-video-o1",
        label: "Kling Video v1",
        tag: "Cost Effective",
        description: "18 credits/sec · Max 10s",
        maxDuration: 10,
        creditCostPerSecond: 18,
        info: "A cost-effective product video model for short marketing clips with controlled motion.",
    },
    {
        value: "kling-v3.0-std",
        label: "Kling Video v3.0 Standard",
        tag: "High Quality",
        description: "21 credits/sec · Max 15s",
        maxDuration: 15,
        creditCostPerSecond: 21,
        info: "A balanced product video model designed for polished motion, product presentation and longer clips.",
    },
    {
        value: "kling-v3.0-pro",
        label: "Kling Video v3.0 Pro",
        tag: "Best Quality",
        description: "28 credits/sec · Max 15s",
        maxDuration: 15,
        creditCostPerSecond: 28,
        info: "The premium Kling option for detailed product presentation, smooth motion and higher visual quality.",
    },
];

const DURATION_OPTIONS = [5, 10, 15];

const PACKAGE_RATES = {
    package1: 0.02,
    package2: 0.016,
    package3: 0.012,
};

const STORAGE_KEY = "active_product_video_ai";
const UNVIEWED_KEY = "unviewed_product_video_ai";

const NewProductVideoAI = () => {
    const [productImage, setProductImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [model, setModel] = useState("");
    const [duration, setDuration] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [generatedVideo, setGeneratedVideo] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [selectedModelInfo, setSelectedModelInfo] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const fileInputRef = useRef(null);
    const generatorRef = useRef(null);
    const showcaseRef = useRef(null);
    const videoRef = useRef(null);

    const { AIuser, userRefetch, setUserRefetch } =
        useContext(UserAuthContext);
    const navigate = useNavigate();

    const selectedModelObj = MODEL_OPTIONS.find(
        (item) => item.value === model
    );

    const maxDuration = selectedModelObj?.maxDuration || 15;

    const totalCredits =
        selectedModelObj && duration
            ? selectedModelObj.creditCostPerSecond * Number(duration)
            : 0;

    const creditCostUSD1 = totalCredits * PACKAGE_RATES.package1;
    const creditCostUSD2 = totalCredits * PACKAGE_RATES.package2;
    const creditCostUSD3 = totalCredits * PACKAGE_RATES.package3;

    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser.credits), 10).toLocaleString()
        : "0";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (duration && Number(duration) > maxDuration) {
            setDuration("");
        }
    }, [model, maxDuration, duration]);

    const clearActiveTransaction = (transactionId) => {
        const activeString = localStorage.getItem(STORAGE_KEY);

        if (!activeString) return;

        try {
            const active = JSON.parse(activeString);

            if (!transactionId || active?.transactionId === transactionId) {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
        }
    };

    const markVideoViewed = async (transactionId) => {
        if (!transactionId) return;

        try {
            await fetch(`${API_BASE}/mark-viewed`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bearer ${localStorage.getItem("aiUser")}`,
                },
                body: JSON.stringify({ transactionId }),
            });
        } catch (error) {
            console.error(
                "[Product Video AI] Error marking video viewed:",
                error
            );
        }
    };

    const getVideoUrl = (data) =>
        data?.videoUrl ||
        data?.data?.videoUrl ||
        data?.data?.video?.videoUrl ||
        data?.video?.videoUrl ||
        null;

    const handleCompletedVideo = async (videoData) => {
        const videoUrl = getVideoUrl(videoData);

        if (!videoUrl) return;

        const transactionId =
            videoData?.transactionId ||
            videoData?.data?.transactionId ||
            null;

        setGeneratedVideo({
            ...videoData,
            transactionId,
            videoUrl,
            status: "completed",
        });

        setIsLoading(false);
        clearActiveTransaction(transactionId);
        localStorage.removeItem(UNVIEWED_KEY);
        setUserRefetch((previous) => !previous);

        // Do not block the UI.
        markVideoViewed(transactionId);
    };

    const handleFailedVideo = (data) => {
        setIsLoading(false);
        clearActiveTransaction(data?.transactionId);

        swal({
            text:
                data?.message ||
                "Product Video generation failed. Please try again.",
            icon: "error",
            buttons: "Ok",
            className: "modal_class_success",
        });
    };

    useEffect(() => {
        let pollingTimer = null;
        let isChecking = false;
        let mounted = true;

        const checkActiveProductVideo = async () => {
            if (!mounted || isChecking) return;

            const activeString = localStorage.getItem(STORAGE_KEY);

            if (!activeString) return;

            let stored;

            try {
                stored = JSON.parse(activeString);
            } catch {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }

            if (!stored?.transactionId) {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }

            isChecking = true;

            try {
                const response = await fetch(`${API_BASE}/active-status`, {
                    method: "GET",
                    headers: {
                        authorization: `Bearer ${localStorage.getItem(
                            "aiUser"
                        )}`,
                    },
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(
                        `Active status request failed: ${response.status}`
                    );
                }

                const result = await response.json();

                console.log(
                    "[Product Video AI] Active status:",
                    result
                );

                if (
                    result?.transactionId &&
                    result.transactionId !== stored.transactionId
                ) {
                    return;
                }

                if (result?.success && result?.isGenerating) {
                    setIsLoading(true);
                    return;
                }

                if (
                    result?.success &&
                    result?.status === "completed" &&
                    getVideoUrl(result)
                ) {
                    await handleCompletedVideo({
                        ...result,
                        transactionId:
                            result.transactionId || stored.transactionId,
                    });
                    return;
                }

                if (
                    result?.success &&
                    ["failed", "error"].includes(result?.status)
                ) {
                    handleFailedVideo({
                        ...result,
                        transactionId:
                            result.transactionId || stored.transactionId,
                    });
                }
            } catch (error) {
                console.error(
                    "[Product Video AI] Active status error:",
                    error
                );
            } finally {
                isChecking = false;
            }
        };

        const checkUnviewedProductVideo = async () => {
            const unviewedString = localStorage.getItem(UNVIEWED_KEY);

            if (!unviewedString) return false;

            try {
                const unviewed = JSON.parse(unviewedString);

                if (Array.isArray(unviewed) && unviewed.length > 0) {
                    const latest = unviewed[unviewed.length - 1];

                    if (getVideoUrl(latest)) {
                        await handleCompletedVideo(latest);
                        return true;
                    }
                }
            } catch (error) {
                console.error(
                    "[Product Video AI] Error parsing unviewed video:",
                    error
                );
            }

            return false;
        };

        const initialize = async () => {
            const hasUnviewed = await checkUnviewedProductVideo();

            if (!hasUnviewed) {
                await checkActiveProductVideo();
            }
        };

        initialize();

        pollingTimer = setInterval(() => {
            checkActiveProductVideo();
        }, 3000);

        const handleBackendTaskCompleted = (event) => {
            const data = event.detail;

            console.log(
                "[Product Video AI] Backend completion event:",
                data
            );

            const eventType = data?.type || data?.action || "";

            if (
                (
                    eventType === "product_video_ai_completed" ||
                    eventType === "product_video_completed"
                ) &&
                (
                    data?.status === "completed" ||
                    getVideoUrl(data)
                )
            ) {
                handleCompletedVideo(data);
            }
        };

        const handleBackendTaskFailed = (event) => {
            const data = event.detail;

            console.log(
                "[Product Video AI] Backend failed event:",
                data
            );

            const eventType = data?.type || data?.action || "";

            if (
                eventType === "product_video_ai_failed" ||
                eventType === "product_video_failed"
            ) {
                handleFailedVideo(data);
            }
        };

        window.addEventListener(
            "backend_task_completed",
            handleBackendTaskCompleted
        );

        window.addEventListener(
            "backend_task_failed",
            handleBackendTaskFailed
        );

        const handleWakeUp = () => {
            if (
                document.visibilityState === "visible" ||
                !document.hidden
            ) {
                checkUnviewedProductVideo();
                checkActiveProductVideo();
            }
        };

        document.addEventListener("visibilitychange", handleWakeUp);
        window.addEventListener("focus", handleWakeUp);
        window.addEventListener("pageshow", handleWakeUp);

        return () => {
            mounted = false;

            if (pollingTimer) {
                clearInterval(pollingTimer);
            }

            window.removeEventListener(
                "backend_task_completed",
                handleBackendTaskCompleted
            );

            window.removeEventListener(
                "backend_task_failed",
                handleBackendTaskFailed
            );

            document.removeEventListener(
                "visibilitychange",
                handleWakeUp
            );

            window.removeEventListener("focus", handleWakeUp);
            window.removeEventListener("pageshow", handleWakeUp);
        };
    }, []);

    const setSelectedImage = (file) => {
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            swal({
                text: "Please upload a valid image file.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            swal({
                text: "Image size must be 10MB or less.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        setProductImage(file);

        const reader = new FileReader();

        reader.onloadend = () => {
            setImagePreview(reader.result);
        };

        reader.readAsDataURL(file);
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];

        if (file) {
            setSelectedImage(file);
        }

        event.target.value = "";
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);

        const file = event.dataTransfer.files?.[0];

        if (file) {
            setSelectedImage(file);
        }
    };

    const handleRemoveImage = (event) => {
        event.stopPropagation();

        setProductImage(null);
        setImagePreview(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleModelSelect = (option) => {
        setModel(option.value);

        if (duration && Number(duration) > option.maxDuration) {
            setDuration("");
        }
    };

    const handleGenerate = async () => {
        const availableCredits = Number(AIuser?.credits || 0);

        if (availableCredits < totalCredits) {
            swal({
                text:
                    "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((value) => {
                if (value) navigate("/purchase");
            });

            return;
        }

        if (!productImage) {
            swal({
                text: "Please upload a product image.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (!model) {
            swal({
                text: "Please select a model.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (!duration) {
            swal({
                text: "Please select a duration.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        const confirmed = await swal({
            title: "Ready to Generate?",
            content: {
                element: "div",
                attributes: {
                    innerHTML: `
                        <div class="npva-swal-cost-info">
                            <div>Model: ${selectedModelObj.label}</div>
                            <div>Duration: ${duration}s</div>
                            <div class="npva-swal-total-cost">
                                Estimated Cost: ${totalCredits} Credits
                            </div>
                            <div class="npva-swal-package package-1">
                                Package 1: USD ${creditCostUSD1.toFixed(2)}
                            </div>
                            <div class="npva-swal-package package-2">
                                Package 2: USD ${creditCostUSD2.toFixed(2)}
                            </div>
                            <div class="npva-swal-package package-3">
                                Package 3: USD ${creditCostUSD3.toFixed(2)}
                            </div>
                            <div class="npva-swal-continue">
                                Do you want to continue?
                            </div>
                        </div>
                    `,
                },
            },
            icon: "info",
            buttons: {
                cancel: {
                    text: "Cancel",
                    visible: true,
                    className: "npva-swal-cancel-btn",
                },
                confirm: {
                    text: "Generate Video",
                    className: "va-swal-generate-btn",
                },
            },
            className: "va-generate-confirm-modal",
        });

        if (!confirmed) return;

        setIsLoading(true);
        setGeneratedVideo(null);

        try {
            const formData = new FormData();

            formData.append("credit", String(totalCredits));
            formData.append("model", model);
            formData.append("duration", String(duration));
            formData.append("productImage", productImage);

            const response = await fetch(`${API_BASE}/generate`, {
                method: "POST",
                headers: {
                    authorization: `Bearer ${localStorage.getItem("aiUser")}`,
                },
                body: formData,
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    "Failed to start Product Video generation."
                );
            }

            setUserRefetch((previous) => !previous);

            const transactionId =
                result?.transactionId ||
                result?.data?.transactionId;

            const immediateVideoUrl = getVideoUrl(result);

            // Keep compatibility with a backend response that may already
            // contain the completed video URL.
            if (immediateVideoUrl) {
                await handleCompletedVideo({
                    ...result,
                    transactionId,
                    videoUrl: immediateVideoUrl,
                });
                return;
            }

            if (!transactionId) {
                throw new Error(
                    "Product Video generation started but no transaction ID was returned."
                );
            }

            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    transactionId,
                    aiType: "product-video-ai",
                    startedAt: Date.now(),
                })
            );
        } catch (error) {
            console.error(
                "[Product Video AI] Generation error:",
                error
            );

            setIsLoading(false);

            swal({
                text:
                    error.message ||
                    "Something went wrong! Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        }
    };

    const handleClear = () => {
        if (isLoading) return;

        setProductImage(null);
        setImagePreview(null);
        setModel("");
        setDuration("");
        setGeneratedVideo(null);
        setSelectedModelInfo(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const downloadVideo = async () => {
        const url = generatedVideo?.videoUrl;

        if (!url) return;

        setDownloading(true);

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const blob = await response.blob();
            const pathname = new URL(url).pathname;
            const filename =
                pathname.split("/").pop() || "ProductVideo.mp4";

            saveAs(blob, filename);
        } catch (error) {
            console.error(
                "[Product Video AI] Download failed:",
                error
            );

            swal({
                text: "Failed to download the video.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setDownloading(false);
        }
    };

    const scrollToGenerator = () => {
        generatorRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    const scrollToShowcase = () => {
        showcaseRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    const isFormValid =
        Boolean(productImage) && Boolean(model) && Boolean(duration);

    return (
        <Fragment>
            {/* <PageHeading
                title="PRODUCT VIDEO AI"
                description="Turn product images into professional AI-powered marketing videos."
                onInfoClick={() => setShowPdfModal(true)}
                infoTooltip="See sample product video"
            /> */}

            <SamplePdfModal
                pdfModal={showPdfModal}
                setPdfModal={setShowPdfModal}
                title="What you can do?"
            >
                <ContentProductVideoAi />
            </SamplePdfModal>

            {selectedModelInfo && (
                <div
                    className="npva-info-overlay"
                    onClick={() => setSelectedModelInfo(null)}
                >
                    <div
                        className="npva-info-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="npva-info-header">
                            <div>
                                <span className="npva-info-kicker">
                                    PRODUCT VIDEO AI
                                </span>
                                <h5 className="mb-0">
                                    {selectedModelInfo.label}
                                </h5>
                            </div>

                            <button
                                type="button"
                                className="npva-info-close"
                                onClick={() =>
                                    setSelectedModelInfo(null)
                                }
                            >
                                ×
                            </button>
                        </div>

                        <div className="npva-info-body">
                            <p className="mb-0">
                                {selectedModelInfo.info}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <main className="npva-page">
                {/* Credits */}
                <div className="container npva-container">
                    <div className="d-flex justify-content-center pt-3">
                        <div className="npva-credit-area text-center">
                            {/* <div className="d-flex justify-content-center align-items-center flex-wrap gap-1">
                                <button className="credits-button">
                                    CREDITS : {creditToLocalString}
                                </button>

                                <button
                                    className="buy-credits-button"
                                    onClick={() => navigate("/purchase")}
                                >
                                    BUY CREDITS
                                </button>

                                <button
                                    onClick={() => navigate("/usage")}
                                    className="money-exchange-button"
                                >
                                    <img
                                        src={moneyExchangeIcon}
                                        alt="usage"
                                    />
                                </button>
                            </div> */}

                             <p className="all-page-title all-page-gradient-text text-center my-4">PRODUCT VIDEO AI</p>

                            <div className="credits-actions">

                                <button className="credits-button">
                                    CREDITS : {creditToLocalString}
                                </button>

                                <button
                                    className="buy-credits-button"
                                    onClick={() => navigate("/purchase")}
                                >
                                    BUY CREDITS
                                </button>

                                <button
                                    onClick={() => navigate("/usage")}
                                    className="money-exchange-button"
                                >
                                    <img src={moneyExchangeIcon} alt="usage" />
                                </button>

                                <button
                                    type="button"
                                    className="money-exchange-button"
                                    onClick={() => {
                                        setShowPdfModal(true);
                                    }}

                                >
                                    <img src={sampleIcon} alt="See sample video" />
                                </button>

                            </div>

                            <p className="text-white npva-credit-caption mb-0 mt-2">
                                {totalCredits > 0
                                    ? `${totalCredits} Credits (USD ${creditCostUSD3.toFixed(
                                        2
                                    )} based on Credits Package 3)`
                                    : "Select model & duration to see cost"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Hero */}
                <section className="npva-hero">
                    <div className="container npva-container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-6">
                                <div className="npva-hero-copy">
                                    <h1 className="npva-hero-title">
                                        Turn your product image into{" "}
                                        <span className="npva-gradient-text">
                                            amazing videos
                                        </span>{" "}
                                        with PRODUCT VIDEO AI
                                    </h1>

                                    <p className="npva-hero-subtitle">
                                        Create professional AI-powered
                                        product marketing videos from a
                                        single product image — choose your
                                        model, select the duration, generate
                                        it, and share it.
                                    </p>

                                    <div className="d-flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="npva-cta-primary"
                                            onClick={scrollToGenerator}
                                        >
                                            <AutoAwesomeIcon fontSize="small" />
                                            Start Creating
                                            <ArrowForwardIcon fontSize="small" />
                                        </button>

                                        <button
                                            type="button"
                                            className="npva-cta-secondary"
                                            onClick={scrollToShowcase}
                                        >
                                            See a sample
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-6">
                                <div className="va-hero-visual">
                                    <div className="va-hero-glow"></div>

                                    <div className="va-hero-image-wrap">
                                        <img
                                            src="/productvideoaiC.png"
                                            alt="AI Video Generator"
                                            className="va-hero-image"
                                        />
                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>
                </section>

                {/* Generator */}
                <section
                    className="npva-generator-section"
                    ref={generatorRef}
                >
                    <div className="container npva-container">
                        <div className="npva-section-heading">
                            <h2>
                                Create your product video{" "}
                                <span>with PRODUCT VIDEO AI</span>
                            </h2>
                            <p>
                                Upload your product, choose the AI model and
                                duration, then let AI turn it into a polished
                                marketing video.
                            </p>
                        </div>

                        <div className="row g-4 align-items-start">
                            <div className="col-lg-8">
                                <div className="npva-generator-shell">
                                    {/* Step 1 */}
                                    <div className="npva-step-block">
                                        <div className="npva-step-header">
                                            <span className="npva-step-badge">
                                                1
                                            </span>
                                            <div>
                                                <div className="npva-step-title">
                                                    Upload Product Image
                                                </div>
                                                <div className="npva-step-subtitle">
                                                    Give AI the product image
                                                    you want to turn into a
                                                    marketing video.
                                                </div>
                                            </div>
                                        </div>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            className="d-none"
                                            onChange={handleImageChange}
                                        />

                                        <div
                                            className={`npva-upload-box ${imagePreview
                                                ? "has-file"
                                                : ""
                                                } ${dragActive
                                                    ? "drag-active"
                                                    : ""
                                                }`}
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setDragActive(true);
                                            }}
                                            onDragLeave={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setDragActive(false);
                                            }}
                                            onDrop={handleDrop}
                                        >
                                            {imagePreview ? (
                                                <div className="npva-preview-wrap">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Product preview"
                                                        className="npva-preview-image"
                                                    />

                                                    <div className="npva-preview-overlay">
                                                        <span>
                                                            Click to change
                                                            image
                                                        </span>
                                                    </div>

                                                    <div className="npva-ready-badge">
                                                        ✓
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="npva-remove-image"
                                                        onClick={
                                                            handleRemoveImage
                                                        }
                                                        title="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="npva-upload-empty">
                                                    <div className="npva-upload-orb">
                                                        <CloudUploadOutlinedIcon />
                                                    </div>

                                                    <div className="npva-upload-main">
                                                        Drop your product image
                                                        here
                                                        <button
                                                            type="button"
                                                            className="npva-browse-btn"
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                fileInputRef.current?.click();
                                                            }}
                                                        >
                                                            Browse
                                                        </button>
                                                    </div>

                                                    <div className="npva-upload-sub">
                                                        Use a clear product
                                                        image for better
                                                        composition and visual
                                                        consistency.
                                                    </div>

                                                    <div className="npva-upload-meta">
                                                        <span>JPG</span>
                                                        <span>PNG</span>
                                                        <span>WEBP</span>
                                                        <i />
                                                        <span>
                                                            Max 10MB
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Step 2 */}
                                    <div className="npva-step-block">
                                        <div className="npva-step-header">
                                            <span className="npva-step-badge">
                                                2
                                            </span>
                                            <div>
                                                <div className="npva-step-title">
                                                    Select Product Video AI
                                                    Model
                                                </div>
                                                <div className="npva-step-subtitle">
                                                    Choose the generation
                                                    quality and pricing level.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row g-2">
                                            {MODEL_OPTIONS.map((option) => (
                                                <div
                                                    className="col-md-4"
                                                    key={option.value}
                                                >
                                                    <div
                                                        className={`npva-model-card ${model ===
                                                            option.value
                                                            ? "selected"
                                                            : ""
                                                            }`}
                                                        onClick={() =>
                                                            handleModelSelect(
                                                                option
                                                            )
                                                        }
                                                    >
                                                        <div className="npva-model-radio" />

                                                        <div className="npva-model-name">
                                                            {option.label}
                                                        </div>

                                                        <div className="npva-model-tag">
                                                            {option.tag}
                                                        </div>

                                                        <div className="npva-model-description">
                                                            {
                                                                option.description
                                                            }
                                                        </div>

                                                        <div className="npva-model-price">
                                                            {option.creditCostPerSecond}{" "}
                                                            credits/sec
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="npva-model-info"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                setSelectedModelInfo(
                                                                    option
                                                                );
                                                            }}
                                                            aria-label={`More information about ${option.label}`}
                                                        >
                                                            <InfoIcon fontSize="inherit" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Step 3 */}
                                    <div className="npva-step-block">
                                        <div className="npva-step-header">
                                            <span className="npva-step-badge">
                                                3
                                            </span>
                                            <div>
                                                <div className="npva-step-title">
                                                    Select Video Duration
                                                </div>
                                                <div className="npva-step-subtitle">
                                                    Pick the final video
                                                    length for your selected
                                                    model.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row g-2">
                                            {DURATION_OPTIONS.map((value) => {
                                                const disabled =
                                                    !selectedModelObj ||
                                                    value > maxDuration;

                                                const cost =
                                                    selectedModelObj
                                                        ? selectedModelObj.creditCostPerSecond *
                                                        value
                                                        : 0;

                                                return (
                                                    <div
                                                        className="col-sm-4"
                                                        key={value}
                                                    >
                                                        <button
                                                            type="button"
                                                            disabled={
                                                                disabled
                                                            }
                                                            className={`npva-duration-card ${duration ===
                                                                String(value)
                                                                ? "selected"
                                                                : ""
                                                                } ${disabled
                                                                    ? "disabled"
                                                                    : ""
                                                                }`}
                                                            onClick={() =>
                                                                setDuration(
                                                                    String(
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            <span>
                                                                {value}s
                                                            </span>
                                                            <small>
                                                                {disabled
                                                                    ? "Not available"
                                                                    : selectedModelObj
                                                                        ? `${cost} credits`
                                                                        : "Select a model"}
                                                            </small>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Cost + Actions */}
                                    <div className="npva-cost-bar">
                                        <div>
                                            <span className="npva-cost-label">
                                                Estimated Generation Cost
                                            </span>

                                            <div className="npva-cost-value">
                                                {totalCredits > 0
                                                    ? totalCredits
                                                    : "—"}{" "}
                                                <small>credits</small>
                                            </div>

                                            <div className="npva-cost-packages">
                                                <span className="package-1">
                                                    Package 1: USD{" "}
                                                    {creditCostUSD1.toFixed(2)}
                                                </span>
                                                <span className="package-2">
                                                    Package 2: USD{" "}
                                                    {creditCostUSD2.toFixed(2)}
                                                </span>
                                                <span className="package-3">
                                                    Package 3: USD{" "}
                                                    {creditCostUSD3.toFixed(2)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2 align-items-center">
                                            <button
                                                type="button"
                                                className="npva-clear-btn"
                                                onClick={handleClear}
                                                disabled={
                                                    (!productImage &&
                                                        !model &&
                                                        !duration) ||
                                                    isLoading
                                                }
                                            >
                                                Clear
                                            </button>

                                            <button
                                                type="button"
                                                className="npva-generate-btn"
                                                onClick={handleGenerate}
                                                disabled={
                                                    !isFormValid || isLoading
                                                }
                                            >
                                                <AutoAwesomeIcon fontSize="small" />
                                                {isLoading
                                                    ? "Processing"
                                                    : "Generate Video"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* <div className="npva-generator-note">
                                        AI generation runs in the background.
                                        Please keep this browser tab open
                                        while your Product Video is being
                                        generated.
                                    </div> */}


                                    {/* Generated video */}
                                    {/* {isLoading && (
                                        <div className="npva-loading-state">
                                            <AiFactsDisplay />
                                            <div
                                                className="spinner-border text-light npva-spinner"
                                                role="status"
                                            >
                                                <span className="visually-hidden">
                                                    Loading...
                                                </span>
                                            </div>
                                            <p>
                                                Creating your Product Video. Please do not
                                                navigate away.
                                            </p>
                                        </div>
                                    )} */}

                                    {isLoading && (
                                        <div className="va-progress-card">
                                            <div className="va-progress-orb">
                                                <AutoAwesomeIcon />
                                            </div>

                                            <p className="va-progress-title">
                                                AI is creating your video
                                            </p>

                                            <p className="va-progress-text">
                                                Your video is being generated in the background.
                                                You can safely return later. Once ready, it will
                                                also be available from your Usage page.
                                            </p>

                                            <div className="va-ai-facts">
                                                <AiFactsDisplay />
                                            </div>

                                        </div>
                                    )}

                                    {generatedVideo?.videoUrl && (
                                        <div className="npva-result-section">
                                            <div className="npva-result-header">
                                                <div>
                                                    <span className="npva-result-kicker">
                                                        GENERATION COMPLETE
                                                    </span>
                                                    <h2>Generated Product Video</h2>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="npva-download-btn"
                                                    onClick={downloadVideo}
                                                    disabled={downloading}
                                                >
                                                    <DownloadOutlined fontSize="small" />
                                                    {downloading
                                                        ? "Downloading..."
                                                        : "Download"}
                                                </button>
                                            </div>

                                            <div className="npva-result-video">
                                                <video
                                                    ref={videoRef}
                                                    src={generatedVideo.videoUrl}
                                                    controls
                                                    autoPlay
                                                    muted
                                                    playsInline
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="col-lg-4">
                                <div className="npva-sidebar">
                                    <div className="npva-credit-card">
                                        <div className="npva-sidebar-label">
                                            <BoltIcon fontSize="inherit" />
                                            Your Credits
                                        </div>

                                        <div className="npva-sidebar-credit-value">
                                            {creditToLocalString}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate("/purchase")
                                            }
                                            className="npva-sidebar-buy"
                                        >
                                            Buy Credits
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate("/usage")
                                            }
                                            className="npva-sidebar-usage"
                                        >
                                            View Usage
                                        </button>
                                    </div>

                                    <div className="npva-tip-card">
                                        <div className="npva-tip-icon">
                                            <AutoAwesomeIcon fontSize="small" />
                                        </div>
                                        <div>
                                            <div className="npva-tip-title">
                                                Pro Tip
                                            </div>
                                            <p>
                                                Upload a clear product image
                                                with good lighting and enough
                                                visual space around the
                                                product for better AI motion.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                </section>

                {/* Showcase / sample */}
                <section
                    className="npva-showcase-section"
                    ref={showcaseRef}
                >
                    <div className="container npva-container">
                        <div className="npva-section-heading">
                            <h2>
                                See What{" "}
                                <span>Product Video AI Can Create</span>
                            </h2>
                            <p>
                                From a product image to a polished marketing
                                video — powered by PRODUCT VIDEO AI.
                            </p>
                        </div>

                        <div className="row g-3">
                            <div className="col-lg-8">
                                <div className="npva-showcase-card npva-showcase-main">
                                    <div className="npva-showcase-icon">
                                        <MovieOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>
                                            Turn one product image into
                                            engaging video content
                                        </h3>
                                        <p>
                                            Create product-focused motion,
                                            polished presentation and
                                            marketing-ready visuals without
                                            building the video manually.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="npva-showcase-card npva-showcase-small">
                                    <ShoppingBagOutlinedIcon />
                                    <strong>Product Focused</strong>
                                    <span>
                                        Keep the product at the center of the
                                        generated video.
                                    </span>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="npva-feature-card">
                                    <div className="npva-feature-icon">
                                        <BoltIcon />
                                    </div>
                                    <strong>Fast Workflow</strong>
                                    <span>
                                        Simple upload, model, duration and
                                        generate flow.
                                    </span>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="npva-feature-card">
                                    <div className="npva-feature-icon">
                                        <FavoriteIcon />
                                    </div>
                                    <strong>Made for Creators</strong>
                                    <span>
                                        Designed for product marketing and
                                        social content workflows.
                                    </span>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="npva-feature-card">
                                    <div className="npva-feature-icon">
                                        <StarIcon />
                                    </div>
                                    <strong>Multiple Models</strong>
                                    <span>
                                        Choose between three Kling video
                                        generation options.
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <button
                                type="button"
                                className="npva-sample-btn"
                                onClick={() => setShowPdfModal(true)}
                            >
                                <MovieOutlinedIcon fontSize="small" />
                                See Product Video Samples
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </Fragment>
    );
};

export default NewProductVideoAI;
